// useMercadoChanged — la ÚNICA suscripción de las vistas al evento WS
// `MERCADO_CHANGED` (round 2026-09-11). Antes cada vista escuchaba por su cuenta
// y recargaba con CADA mensaje.
//
// Por qué agrupar: un envío del scraper llega en bloques (~1 por segundo; un
// envío "completo" son cientos) y cada bloque emite su propio MERCADO_CHANGED.
// Sin agrupar, /mercado reiniciaba sus pestañas por bloque (perdiendo lo elegido
// y cerrando el modal de avisos) y varias pestañas abiertas sumaban cientos de
// requests por minuto, que además contaban en el límite global por IP.
//
// Cómo agrupa: acumula lo que dicen los mensajes y llama a `onCambio` UNA vez
// cuando la ráfaga se calma. Si la ráfaga es larga, igual refresca cada
// `MERCADO_MAX_ESPERA_MS` para que un envío de 20 minutos no deje la vista
// congelada. Los cambios hechos por una persona (vínculo, visibilidad, config,
// avisos, borrado) no vienen en ráfaga: esperan poco.
//
// Al RECONECTAR el socket (redeploy, corte de red) avisa como si todo hubiera
// cambiado: los mensajes emitidos mientras estuvo caído se perdieron.
import { useEffect, useRef } from "react";
import { useWebSocket } from "../context/WebSocketContext";

/** Qué cambió desde la última llamada (acumulado de la ráfaga). */
export interface MercadoCambio {
  /** Llegaron filas de una ingesta del scraper. */
  filas: boolean;
  /** Cambió la vinculación código ↔ propiedades (a mano o por la ingesta). */
  mapeo: boolean;
  /** Cambió la configuración del módulo (interruptor maestro, paleta, parámetros). */
  config: boolean;
  /** Cambió la visibilidad de alguna propiedad para su cliente. */
  visibilidad: boolean;
  /** Se creó, editó o borró un aviso. */
  notas: boolean;
  /** Se borraron todos los datos del scraper. */
  borrado: boolean;
}

/** Espera tras el último bloque de una ingesta antes de recargar. */
export const MERCADO_ESPERA_INGESTA_MS = 4000;
/** Espera para cambios hechos por una persona (no vienen en ráfaga). */
export const MERCADO_ESPERA_ACCION_MS = 600;
/** Tope: durante una ráfaga larga, refresca al menos cada este tiempo. */
export const MERCADO_MAX_ESPERA_MS = 30000;

const TODO: MercadoCambio = {
  filas: true,
  mapeo: true,
  config: true,
  visibilidad: true,
  notas: true,
  borrado: true,
};

/** Traduce el payload del mensaje. Un mensaje sin flags conocidos cuenta como datos nuevos. */
function cambioDeMensaje(msg: any): MercadoCambio {
  const cambio: MercadoCambio = {
    filas: Boolean(msg?.filas),
    mapeo: Boolean(msg?.mapeo),
    config: Boolean(msg?.config),
    visibilidad: Boolean(msg?.visibilidad),
    notas: Boolean(msg?.notas),
    borrado: Boolean(msg?.borrado),
  };
  if (!Object.values(cambio).some(Boolean)) cambio.filas = true;
  return cambio;
}

export function useMercadoChanged(onCambio: (cambio: MercadoCambio) => void): void {
  const { socket } = useWebSocket();
  const onCambioRef = useRef(onCambio);
  onCambioRef.current = onCambio;
  const prevSocketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!socket) return;
    let acumulado: MercadoCambio | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inicioRafaga = 0;

    const disparar = () => {
      timer = null;
      inicioRafaga = 0;
      const cambio = acumulado;
      acumulado = null;
      if (cambio) onCambioRef.current(cambio);
    };

    const acumular = (nuevo: MercadoCambio) => {
      const base = acumulado;
      acumulado = base
        ? {
            filas: base.filas || nuevo.filas,
            mapeo: base.mapeo || nuevo.mapeo,
            config: base.config || nuevo.config,
            visibilidad: base.visibilidad || nuevo.visibilidad,
            notas: base.notas || nuevo.notas,
            borrado: base.borrado || nuevo.borrado,
          }
        : { ...nuevo };
      const ahora = Date.now();
      if (!inicioRafaga) inicioRafaga = ahora;
      if (timer) clearTimeout(timer);
      if (ahora - inicioRafaga >= MERCADO_MAX_ESPERA_MS) {
        disparar();
        return;
      }
      timer = setTimeout(
        disparar,
        acumulado.filas ? MERCADO_ESPERA_INGESTA_MS : MERCADO_ESPERA_ACCION_MS,
      );
    };

    // Reconexión: había un socket antes y este es otro.
    const esReconexion = prevSocketRef.current != null && prevSocketRef.current !== socket;
    prevSocketRef.current = socket;
    if (esReconexion) acumular(TODO);

    const onMessage = (e: MessageEvent) => {
      let msg: any;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      if (msg?.type !== "MERCADO_CHANGED") return;
      acumular(cambioDeMensaje(msg));
    };

    socket.addEventListener("message", onMessage);
    return () => {
      socket.removeEventListener("message", onMessage);
      if (timer) clearTimeout(timer);
    };
  }, [socket]);
}
